
import {throwError as observableThrowError,  Observable } from 'rxjs';
import { Http, Request, RequestOptions, RequestOptionsArgs, Response, ConnectionBackend, Headers } from "@angular/http";
import "rxjs/add/observable/fromPromise";
import "rxjs/add/operator/mergeMap";

import { Account } from './account.service';
import { Token } from '../models/token';
import { Config } from '../config';
import { TokenHelper } from '../shared/token_helper';
import { Injector, Injectable } from '@angular/core';
import { OAuthToken } from '../models/oauth-token';


export interface InterceptorConfigOptional {
	headerName?: string;
	headerPrefix?: string;
	noTokenError?: boolean;
}

const DEFAULT_HEADER_NAME = 'Authorization';
const DEFAULT_HEADER_PREFIX_BEARER = 'Bearer';

export class InterceptorConfig {

	headerName: string = DEFAULT_HEADER_NAME;
	headerPrefix: string = DEFAULT_HEADER_PREFIX_BEARER;
	noTokenError: boolean = false;

	constructor(config?: InterceptorConfigOptional) {
		config = config || {};
		(<any>Object).assign(this, config)
	}
}

export abstract class HttpInterceptor extends Http {

	private origRequest: Request;

	constructor(backend: ConnectionBackend, defaultOptions: RequestOptions, private config: InterceptorConfig) {
		super(backend, defaultOptions);
	}

	private getRequestOptionArgs(options?: RequestOptionsArgs): RequestOptionsArgs {
		if (options == null) {
			options = new RequestOptions();
		}
		if (options.headers == null) {
			options.headers = new Headers();
		}
		options.headers.append('Content-Type', 'application/json');
		return options;
	}

	protected requestWithToken(req: Request, token: Token): Observable<Response> {
		this.origRequest = req;
		if (!this.config.noTokenError && !token) {
			return observableThrowError(new Error('No authorization token given'));
		} else {
			req.headers.set(this.config.headerName, this.config.headerPrefix + ' ' + token.accessToken);
		}

		return super.request(req);
	}

	request(url: string | Request, options?: RequestOptionsArgs): Observable<Response> {
		if (typeof url === 'string') {
			return this.get(url, options);
		}
		let req: Request = url as Request;
		let token = this.getToken();
		return token.mergeMap((token: Token) => this.requestWithToken(req, token));
	}

	get(url: string, options?: RequestOptionsArgs, noIntercept?: boolean): Observable<Response> {
		if (noIntercept) {
			return super.get(url, options);
		}
		return this.intercept(super.get(url, options));
	}

	post(url: string, body: any, options?: RequestOptionsArgs, noIntercept?: boolean): Observable<Response> {
		if (noIntercept) {
			return super.post(url, body, options);
		}
		return this.intercept(super.post(url, body, this.getRequestOptionArgs(options)));
	}

	put(url: string, body: any, options?: RequestOptionsArgs, noIntercept?: boolean): Observable<Response> {
		if (noIntercept) {
			return super.put(url, body, options);
		}
		return this.intercept(super.put(url, body, this.getRequestOptionArgs(options)));
	}

	delete(url: string, options?: RequestOptionsArgs, noIntercept?: boolean): Observable<Response> {
		if (noIntercept) {
			return super.delete(url, options);
		}
		return this.intercept(super.delete(url, options));
	}

	protected intercept(observable: Observable<Response>): Observable<Response> {
		return observable.catch((err, source) => {
			if (this.isErrUnauthorized(err)) {
				console.log("Unauthorised need to refresh token");

				// check if we have refreshToken if yes do a refresh else return the error
				return this.getToken()
					.mergeMap(tk => {
						let t = new Token();
						if (tk) {
							t = tk;
						}

						if (t.refreshToken !== '') {

							let orig = this.origRequest;
							return this.refreshToken()
								.catch(err => {
									// check in case of an error could not get token due to deletion or token into db
									if (err.message && err.message.indexOf('cannot fetch token') !== -1) {
										return observableThrowError(new Error('unauthorized'));
									}
									return observableThrowError(err);
								})
								.mergeMap(data => {
									if (data && data.accessToken) {

										let th = new TokenHelper();
										t.accessToken = data.accessToken;
										t.refreshToken = data.refreshToken;
										t.tokenType = data.tokenType;
										t.expiresIn = data.expiresIn;
										t.expiresAt = th.getExpiresAt(data.expiresIn);

										return this.saveToken(t);
									}
									return observableThrowError(new Error('unauthorized'));
								})
								.mergeMap(token => {
									return this.requestWithToken(orig, token);
								});
						}
						return observableThrowError(err);
					});
			}
			return observableThrowError(err);
		});
	}

	protected abstract getToken(): Observable<Token>;

	protected abstract saveToken(token: Token): Observable<Token>;

	protected abstract refreshToken(): Observable<OAuthToken>;

	protected abstract isErrUnauthorized(resp: Response): boolean;
}


@Injectable()
export class HttpService extends HttpInterceptor {

	constructor(backend: ConnectionBackend, defaultOptions: RequestOptions, private injector: Injector) {
		super(backend, defaultOptions, new InterceptorConfig({ noTokenError: true }));
	}

	protected getToken(): Observable<Token> {
		let t = this.account().getToken();
		if (!t) {
			t = new Token();
		}

		return Observable.of(t);
	}

	protected saveToken(token: Token): Observable<Token> {
		this.account().setToken(token);
		return Observable.of(token);
	}

	protected refreshToken(): Observable<OAuthToken> {
		return this.getToken()
			.mergeMap(t => {
				return super.post(`${this.cfg().identityApiUrl}/oauth2/token`, { refreshToken: t.refreshToken }, null, true)
					.map(resp => {
						if (resp.status == 200) {
							return resp.json()
						} else {
							throw this.getError(resp);
						}
					})
					.catch(err => {
						let er = this.getError(err);
						return observableThrowError(er);
					});
			});
	}

	protected account(): Account {
		return this.injector.get(Account);
	}

	protected cfg(): Config {
		return this.injector.get(Config);
	}

	protected isErrUnauthorized(resp: Response): boolean {
		let err = this.getError(resp);
		if (err.message == 'unauthorized') {
			return true;
		}
		return false;
	}

	private getError(err: any): Error {
		var er: any;
		var msg = 'no connection';
		if (typeof (err.json) === 'function') {
			er = err.json();
			if (er.message != null && er.message != null && er.message != '') {
				msg = er.message;
			} else if (er.error != null && er.error != null && er.error != '') {
				msg = er.error;
			} else {
				console.log(err);
			}
			return new Error(msg);
		} else if (err instanceof Error) {
			return err;
		} else {
			console.log(err);
		}
	}
}


export function getHttpService(backend: ConnectionBackend, defaultOptions: RequestOptions, injector: Injector) {
	return new HttpService(backend, defaultOptions, injector);
}
